import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAccess } from '../auth/access';
import { generalHelp, HELP_TOPICS, HelpTopic, topicFor, topicHome, topicText } from './index';

/**
 * The Help button in the header, on every screen. It opens the guide for the page you are on
 * (F1 does the same); the search box finds the guide of any other screen your role can open.
 */
export default function HelpButton() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const access = useAccess();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  /** A guide picked from the search list; null shows the current page's guide. */
  const [picked, setPicked] = useState<HelpTopic | null>(null);

  const current = topicFor(pathname) ?? generalHelp;
  const shown = picked ?? current;

  // A new page shows its own guide.
  useEffect(() => {
    setPicked(null);
    setQuery('');
  }, [pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'F1') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** Guides of the screens this login can open: admin all; others those under a granted menu, plus screens no menu leads to (e.g. Change Password). */
  const available = useMemo(() => {
    if (access?.admin) return HELP_TOPICS;
    const menuPaths = (access?.menus ?? []).map((m) => m.path);
    const under = (home: string, p: string) => (p === '/' ? home === '/' : home === p || home.startsWith(`${p}/`));
    return HELP_TOPICS.filter((t) => {
      const home = topicHome(t);
      if (menuPaths.some((p) => under(home, p))) return true;
      return !KNOWN_MENU_PREFIXES.some((p) => home.startsWith(p));
    });
  }, [access]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const words = q.split(/\s+/);
    return [generalHelp, ...available].filter((t) => {
      const text = topicText(t);
      return words.every((w) => text.includes(w));
    });
  }, [query, available]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <Button
        variant='outlined'
        size='small'
        startIcon={<Icon icon='mdi:help-circle-outline' />}
        onClick={() => setOpen(true)}
        title='Help for this screen (F1)'
        sx={{ borderRadius: 5, textTransform: 'none' }}
      >
        Help
      </Button>

      <Drawer anchor='right' open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: { xs: '100vw', sm: 480 } } }}>
        <Stack sx={{ height: '100%' }}>
          <Stack direction='row' alignItems='center' gap={1} sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
            <Icon icon='mdi:help-circle-outline' width={22} />
            <Typography variant='h6' sx={{ flex: 1 }}>
              Help
            </Typography>
            <IconButton aria-label='Close help' onClick={() => setOpen(false)}>
              <Icon icon='mdi:close' />
            </IconButton>
          </Stack>
          <Box sx={{ px: 3, pb: 2 }}>
            <TextField
              size='small'
              fullWidth
              placeholder='Search all help (e.g. "leave", "voucher", "PF")'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Icon icon='mdi:magnify' />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
            {query.trim() ? (
              <>
                <Typography variant='overline' color='text.secondary'>
                  {results.length} guide{results.length === 1 ? '' : 's'} found
                </Typography>
                <List dense>
                  {results.map((t) => (
                    <ListItemButton
                      key={t.title}
                      onClick={() => {
                        setPicked(t);
                        setQuery('');
                      }}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemText primary={t.title} secondary={t.summary} />
                    </ListItemButton>
                  ))}
                </List>
              </>
            ) : (
              <TopicView topic={shown} isCurrent={shown === current} onBack={picked ? () => setPicked(null) : undefined} onGo={go} />
            )}
          </Box>
        </Stack>
      </Drawer>
    </>
  );
}

/** Main menu paths: a guide whose screen sits under one of these needs that menu to be listed. */
const KNOWN_MENU_PREFIXES = [
  '/dashboard', '/my-', '/timesheet', '/work', '/site-visits', '/jobs', '/tasks', '/workload', '/team-timesheets',
  '/employees', '/admin-module', '/employee-records', '/departments', '/attendance-register', '/leave-approvals',
  '/holidays', '/letters-certificates', '/discipline-board', '/hiring-request', '/candidates', '/payroll',
  '/pay-profiles', '/salary-board', '/bills', '/outstanding', '/payments', '/reimbursement-approvals', '/accounts',
  '/update-data', '/contact-submissions', '/company-master', '/contact-masters', '/reference-data', '/users', '/roles',
  '/files',
];

function TopicView({
  topic,
  isCurrent,
  onBack,
  onGo,
}: {
  topic: HelpTopic;
  isCurrent: boolean;
  onBack?: () => void;
  onGo: (path: string) => void;
}) {
  return (
    <Stack gap={2.5}>
      {onBack && (
        <Button size='small' startIcon={<Icon icon='mdi:arrow-left' />} onClick={onBack} sx={{ alignSelf: 'flex-start' }}>
          Back to this screen's guide
        </Button>
      )}
      <Box>
        <Typography variant='h5' fontWeight={700}>
          {topic.title}
        </Typography>
        <Typography color='text.secondary' sx={{ mt: 0.5 }}>
          {topic.summary}
        </Typography>
        {!isCurrent && topic !== generalHelp && (
          <Button size='small' sx={{ mt: 1, px: 0 }} endIcon={<Icon icon='mdi:arrow-right' />} onClick={() => onGo(topicHome(topic))}>
            Open this screen
          </Button>
        )}
      </Box>

      {topic.sections.map((s) => (
        <Box key={s.title}>
          <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 0.75 }}>
            {s.title}
          </Typography>
          {s.body && (
            <Typography variant='body2' sx={{ mb: 1, whiteSpace: 'pre-line' }}>
              {s.body}
            </Typography>
          )}
          {s.steps && (
            <Box component='ol' sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
              {s.steps.map((step, i) => (
                <Typography component='li' variant='body2' key={i}>
                  {step}
                </Typography>
              ))}
            </Box>
          )}
          {s.fields && (
            <Stack gap={1} sx={{ mt: s.steps ? 1 : 0 }}>
              {s.fields.map((f) => (
                <Box key={f.name} sx={{ pl: 1.5, borderLeft: '3px solid', borderColor: f.required ? 'primary.main' : 'divider' }}>
                  <Stack direction='row' alignItems='center' gap={1}>
                    <Typography variant='body2' fontWeight={600}>
                      {f.name}
                    </Typography>
                    {f.required && <Chip label='required' size='small' color='primary' variant='outlined' sx={{ height: 18, fontSize: 11 }} />}
                  </Stack>
                  <Typography variant='body2' color='text.secondary'>
                    {f.help}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
          {s.bullets && (
            <Box component='ul' sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
              {s.bullets.map((b, i) => (
                <Typography component='li' variant='body2' key={i}>
                  {b}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      ))}

      {topic.related && topic.related.length > 0 && (
        <Box>
          <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 0.75 }}>
            Related screens
          </Typography>
          <Stack direction='row' gap={1} flexWrap='wrap'>
            {topic.related.map((r) => (
              <Chip key={r.path + r.label} label={r.label} onClick={() => onGo(r.path)} icon={<Icon icon='mdi:arrow-right' />} />
            ))}
          </Stack>
        </Box>
      )}
      <Typography variant='caption' color='text.secondary' sx={{ pt: 1 }}>
        Press F1 to open or close help.
      </Typography>
    </Stack>
  );
}
